import React, { memo, useRef, useState, FC } from 'react';
import {
  AccordionLegacy,
  AccordionSummary,
  AccordionDetails,
  BLUE,
  Box,
  Button,
  ButtonGroup,
  Container,
  ExpandMore,
  GRAY,
  Grid,
  icons,
  Link,
  makeStyles,
  Menu,
  MenuIcon,
  MenuItem,
  Typography,
  WHITE,
} from '@applyboard/ui-components';
import * as CONSTS from './consts';

const { ApplyBoardLogoIcon } = icons;
const useStyles = makeStyles({
  box: {
    backgroundColor: WHITE,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.03)',
    zIndex: '5',
    position: 'relative',
  },
  container: {
    maxWidth: '1440px',
    padding: '0px 20px !important',
    '@media (max-width: 600px)': {
      padding: '0px !important',
    },
  },
  height: {
    height: '94px',
    '@media (max-width: 991px)': {
      height: '70px',
    },
  },
  text: {
    fontFamily: 'Montserrat !important',
    fontWeight: '600 !important',
    fontSize: '16px !important',
    lineHeight: '28px !important',
  },
  logo: {
    width: '210px',
    '@media (max-width: 991px)': {
      width: '177px',
    },
    '@media (max-width: 600px)': {
      marginLeft: '20px',
    },
  },
  link: {
    '& p': {
      padding: '0px 20px',
      '&:hover': {
        color: GRAY[5],
      },
    },
  },
  button: {
    '& .MuiButton-sizeSmall': {
      padding: '8px 24px',
      maxHeight: '40px',
      border: '2px solid',
      borderColor: `${BLUE[10]} !important`,
      borderRadius: '5px !important',
    },
    '& .MuiButton-outlined': {
      marginRight: '13px',
      '&:hover': {
        backgroundColor: `${BLUE[10]} !important`,
        color: `${WHITE} !important`,
      },
    },
    '& .MuiButton-contained': {
      '&:hover': {
        backgroundColor: `${WHITE} !important`,
        color: `${BLUE[10]} !important`,
      },
    },
  },
  menuButton: {
    padding: '0px !important',
    marginRight: '18px !important',
    minWidth: '40px !important',
    minHeight: '40px !important',
    borderRadius: '5px !important',
    '@media (max-width: 600px)': {
      margin: '0px !important',
      width: '80px !important',
      height: '70px !important',
      borderRadius: '0px !important',
    },
  },
  menu: {
    '& .MuiAccordion-root': {
      boxShadow: 'none',
      marginTop: '0px',
      '& .MuiAccordionSummary-root': {
        padding: '0',
        '& .MuiAccordionSummary-content': {
          flexGrow: '0',
        },
        '& .MuiAccordionSummary-expandIcon': {
          color: BLUE[10],
          padding: '0',
          marginLeft: '-24px',
          justifyContent: 'start',
        },
      },
      '& .MuiAccordionDetails-root': {
        padding: '0',
        flexDirection: 'column',
        '& p': {
          fontWeight: '500 !important',
          color: GRAY[5],
        },
        '@media (min-width: 600.01px)': {
          '& .MuiButtonBase-root': {
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        },
      },
      '& .MuiButtonBase-root': {
        maxHeight: '44px',
        flex: '1',
      },
    },
    '& .MuiMenuItem-root': {
      minHeight: 'auto',
      '&:hover': {
        '& p': {
          color: GRAY[5],
        },
        '& .MuiAccordionSummary-expandIcon': {
          color: `${GRAY[5]} !important`,
        },
      },
      '& .MuiTypography-root': {
        padding: '0px 24px',
      },
    },
    '& .MuiAccordion-root:before': {
      height: '0',
    },
    '& .MuiPaper-root': {
      background: WHITE,
      '& .MuiListItem-root': {
        background: 'inherit !important',
      },
      '& .MuiList-root': {
        '& > div': {
          padding: '14px 0px',
        },
      },
    },
    '& .MuiPaper-elevation1': {
      borderRadius: '5px',
      marginTop: '0px',
    },
    '& .Mui-expanded': {
      marginBottom: '0px',
      marginTop: '0px',
      transform: 'rotate(0deg)',
      '& .MuiSvgIcon-root': {
        transform: 'rotate(180deg)',
      },
    },
    '& > div': {
      top: '27px !important',
      left: '45px !important',
      '@media (max-width: 991px)': {
        top: '148px !important',
        left: '0px !important',
        transform: 'revert !important',
        width: '100%',
        willChange: 'revert !important',
      },
    },
  },
  header: {
    '& p': {
      color: BLUE[10],
    },
  },
  hideButtonGroup: {
    '@media (max-width: 991px)': {
      display: 'none !important',
    },
    '& p': {
      color: BLUE[10],
    },
  },
  showButtonGroup: {
    display: 'none !important',
    '@media (max-width: 991px)': {
      display: 'block !important',
      '& p': {
        color: BLUE[10],
      },
    },
  },
  hideLoginSection: {
    '@media (max-width: 600px)': {
      display: 'none !important',
    },
  },
  showLoginSection: {
    display: 'none !important',
    '@media (max-width: 600px)': {
      display: 'flex !important',
      margin: '8px 16px 8px 40px',
    },
  },
});

type NavLink = {
  text: string;
  link: string;
};

type HeaderProps = {
  showLoginGroup?: boolean;
};

const Header: FC<HeaderProps> = ({ showLoginGroup = true }) => {
  const classes = useStyles();

  const createLink = (title: string, url: string) => {
    const newLink: NavLink = {
      text: title,
      link: url,
    };

    return newLink;
  };

  const studentLink = createLink('Students', CONSTS.STUDENTS_URL);

  const recruitersLink = createLink('Recruitment Partners', CONSTS.RECRUIT_URL);

  const schoolLink = createLink('Schools', CONSTS.SCHOOLS_URL);

  const solutionsLink = createLink('Our Solutions', CONSTS.SOLUTIONS_URL);

  const storyLink = createLink('Our Story', CONSTS.ABOUT_URL);

  const careerLink = createLink('Careers', CONSTS.CAREERS_URL);

  const pressLink = createLink('Press', CONSTS.PRESS_URL);

  const contactLink = createLink('Contact', CONSTS.CONTACT_URL);

  const loginLink = createLink('Log In', CONSTS.LOGIN_URL);

  const registerLink = createLink('Register', CONSTS.REGISTER_URL);

  const createProductLink = (navLink: NavLink) => {
    const Component = (
      <Box>
        <Link href={navLink.link} underline="none" className={classes.link}>
          <Typography className={classes.text}>{navLink.text}</Typography>
        </Link>
      </Box>
    );

    return Component;
  };

  const createAccountButton = (navLink: NavLink, type: string) => {
    const Component = (
      <Box className={classes.button}>
        <Button component="a" href={navLink.link} variant={type} size="small">
          <Typography className={classes.text}>{navLink.text}</Typography>
        </Button>
      </Box>
    );

    return Component;
  };

  const createMenuItems = (items: Array<{ text: string; link: string }>) => {
    const Component = (
      <>
        {items.map((item, idx) => (
          <MenuItem component="a" href={item.link} key={idx}>
            <Typography className={classes.text}>{item.text}</Typography>
          </MenuItem>
        ))}
      </>
    );

    return Component;
  };

  const NavMenu = (props: {
    showLoginGroup: boolean;
    productItems: Array<{ text: string; link: string }>;
    aboutItems: Array<{ text: string; link: string }>;
  }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLButtonElement>();
    return (
      <Box className={classes.menu}>
        <Button
          className={classes.menuButton}
          variant="text"
          aria-haspopup
          aria-expanded={open}
          onClick={() => {
            setOpen(!open);
          }}
          style={{ backgroundColor: open ? BLUE[10] : 'transparent' }}
          ref={ref}
        >
          <MenuIcon
            style={{ color: open ? WHITE : BLUE[10], verticalAlign: 'middle' }}
            fontSize="large"
          />
        </Button>
        <Menu anchorRef={ref} open={open} setOpen={setOpen}>
          <Box className={classes.showButtonGroup}>
            {createMenuItems(props.productItems)}
            <AccordionLegacy>
              <MenuItem>
                <AccordionSummary className={classes.header} expandIcon={<ExpandMore />}>
                  <Typography className={classes.text}>About</Typography>
                </AccordionSummary>
              </MenuItem>
              <AccordionDetails>{createMenuItems(props.aboutItems)}</AccordionDetails>
            </AccordionLegacy>
          </Box>
          <Box className={classes.hideButtonGroup}>{createMenuItems(props.aboutItems)}</Box>
          <MenuItem className={classes.header} component="a" href={CONSTS.BLOG_URL}>
            <Typography className={classes.text}>Blog</Typography>
          </MenuItem>
          {props.showLoginGroup && (
            <ButtonGroup className={classes.showLoginSection}>
              {createAccountButton(loginLink, 'outlined')}
              {createAccountButton(registerLink, 'contained')}
            </ButtonGroup>
          )}
        </Menu>
      </Box>
    );
  };

  return (
    <Box className={classes.box}>
      {/* TODO: we have kept the code for NewServices navbar (second navbar)
                in case we want to render it again in the future to promote
                new links/services. To render it, we just need to uncomment
                the following line and import the component from './NewServices'.
      <NewServices /> */}
      <Container maxWidth={false} className={`${classes.container} ${classes.height}`}>
        <Grid
          className={classes.height}
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item>
            <Box className={classes.logo}>
              <Link href={CONSTS.APPLYBOARD_URL}>
                <ApplyBoardLogoIcon color="blue" style={{ verticalAlign: 'middle' }} />
              </Link>
            </Box>
          </Grid>

          <Grid item>
            <ButtonGroup className={classes.hideButtonGroup}>
              {createProductLink(studentLink)}
              {createProductLink(recruitersLink)}
              {createProductLink(schoolLink)}
            </ButtonGroup>
          </Grid>

          <Grid item>
            <ButtonGroup>
              <NavMenu
                showLoginGroup={showLoginGroup}
                productItems={[studentLink, recruitersLink, schoolLink]}
                aboutItems={[solutionsLink, storyLink, careerLink, pressLink, contactLink]}
              />
            </ButtonGroup>
            {showLoginGroup && (
              <ButtonGroup className={classes.hideLoginSection}>
                {createAccountButton(loginLink, 'outlined')}
                {createAccountButton(registerLink, 'contained')}
              </ButtonGroup>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export const Nav = memo(Header);
